import { CSVLink } from "react-csv";
import { Column } from "primereact/column";
import { TreeTable } from "primereact/treetable";
import { TreeNode } from "rsuite/esm/internals/Tree/types";
import { numberFormatterOnlyInM } from "../../../utils/formatter";
import { EmptyMessageTemplate } from "../../compliance-monitoring/cost-compliance";
import { useEffect, useState } from "react";

const getColorForKey = (key: string) => {
  // Split the key by dashes to determine its depth
  const depth = key?.split("-").length;

  // Assign colors based on depth
  if (key === "-1") {
    return "#629309"; // Base case for key "0"
  } else if (depth === 1 && key !== "-1") {
    return "#86BC25"; // Keys like "0-1", "0-2"
  } else if (depth === 2) {
    return "#A4C073"; // Lighter green for keys like "0-0-1", "0-0-2"
  } else {
    return "#629309"; // Default color for other cases
  }
};

const lbsBodyTemplate = (node: any, totalVolume: number) => {
  const maxLbs = totalVolume; // Maximum lbs value for scaling
  const percentage = (node?.volume / maxLbs) * 100;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-[0.6] h-6 bg-gray-100 rounded-md">
        <div
          className="absolute h-full rounded-md"
          style={{
            width: `${percentage}%`,
            background: getColorForKey(node?.key),
          }}
        />
      </div>
      <span className="flex-[0.4] text-right">
        {numberFormatterOnlyInM(node?.volume, 1)}
      </span>
    </div>
  );
};

const spendBodyTemplate = (node: any, totalSpend: number) => {
  const maxSpend = totalSpend; // Maximum spend value for scaling
  const percentage = (node?.spend / maxSpend) * 100;

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-[0.6] h-6 bg-gray-100 rounded-md">
        <div
          className="absolute h-full rounded-md"
          style={{
            width: `${percentage}%`,
            background: getColorForKey(node?.key),
          }}
        />
      </div>
      <span className="flex-[0.4] text-right">
        {numberFormatterOnlyInM(node?.spend, 1, "$")}
      </span>
    </div>
  );
};

// const formattedData = (
//   data: any,
//   parentName = "",
//   result: any = [],
//   totals = { grandTotalSpend: 0, grandTotalVol: 0 }, // Use an object to track totals
//   isRoot = true // Flag to check if it's the root level
// ) => {
//   data.forEach(
//     (item: {
//       name: any;
//       spend: number;
//       volume: number;
//       children: any;
//       key: any;
//     }) => {
//       const { name, spend, volume, children, key } = item;
//       if (key !== "-1" && key?.split("")?.length !== 1) {
//         const row = {
//           l1: key.includes("-") ? parentName : "",
//           l2: name,
//           spend: spend.toLocaleString("en-US", {
//             style: "currency",
//             currency: "USD", // or "TRY" for Turkish Lira
//           }),
//           volume: Math.trunc(volume),
//         };
//         // Update totals
//         totals.grandTotalSpend += spend;
//         totals.grandTotalVol += Math.trunc(volume);
//         result.push(row as any);
//       }

//       if (children && children.length > 0) {
//         formattedData(children, name, result, totals, false);
//         if (key !== "-1") {
//           result.push({
//             l1: "Sub Total",
//             l2: "",
//             spend: item?.spend.toLocaleString("en-US", {
//               style: "currency",
//               currency: "USD", // or "TRY" for Turkish Lira
//             }),
//             volume: Math.trunc(item?.volume),
//           });
//         }
//       }
//     }
//   );

//   // Add the grand total row only at the root level
//   if (isRoot) {
//     result.push({
//       l1: "Grand Total",
//       l2: "",
//       spend: totals.grandTotalSpend.toLocaleString("en-US", {
//         style: "currency",
//         currency: "USD", // or "TRY" for Turkish Lira
//       }),
//       volume: totals.grandTotalVol,
//     });
//   }

//   return result;
// };

// ...existing code...

// ...existing code...

const formattedData = (
  data: any[],
  parents: string[] = [], // [l1, l2]
  result: any[] = [],
  totals = { grandTotalSpend: 0, grandTotalVol: 0 },
  isRoot = true,
) => {
  data.forEach(
    (item: {
      name: any;
      spend: number;
      volume: number;
      children: any[];
      key: string;
    }) => {
      const { name, spend, volume, children, key } = item;
      const isRootNode = key === "-1";
      const depth = key ? key.split("-").length : 0;

      // Skip parent summary rows (depth === 2) to avoid empty l3 in CSV
      if (!isRootNode && depth >= 3) {
        const [l1 = "", l2 = ""] = parents;
        const safeSpend = spend ?? 0;
        const safeVolume = volume ?? 0;
        const row = {
          l1, // top-level category
          l2, // second-level category
          l3: name, // third-level
          spend: safeSpend.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          volume: Math.trunc(safeVolume),
        };
        totals.grandTotalSpend += safeSpend;
        totals.grandTotalVol += Math.trunc(safeVolume);
        result.push(row as any);
      }

      if (children && children.length > 0) {
        // Build next parents stack for deeper levels
        const nextParents =
          depth === 1
            ? [name] // first level below root becomes l1
            : depth >= 2
              ? [parents[0] || "", name] // make current name l2
              : parents;

        formattedData(children, nextParents, result, totals, false);

        // if (!isRootNode) {
        //   result.push({
        //     l1: "Sub Total",
        //     l2: "",
        //     l3: "",
        //     spend: item?.spend.toLocaleString("en-US", { style: "currency", currency: "USD" }),
        //     volume: Math.trunc(item?.volume),
        //   });
        // }
      }
    },
  );

  if (isRoot) {
    result.push({
      l1: "Grand Total",
      l2: "",
      l3: "",
      spend: totals.grandTotalSpend.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      }),
      volume: totals.grandTotalVol,
    });
  }

  return result;
};

const PurchaseSummary = ({
  treeData,
  totalSpend,
  totalVolume,
  purchaseOrSales,
}: any) => {
  const [expandedKeys, setExpandedKeys] = useState({});

  useEffect(() => {
    let obj: any = {};

    if (treeData && treeData?.length > 0 && treeData[0]?.children) {
      treeData[0]?.children?.map((item: any) => {
        obj[item.key] = true;
      });
    }
    setExpandedKeys({ "-1": true, ...obj });
  }, [treeData]);

  const downloadDataBody = formattedData(treeData);
  return (
    <div className="p-[20px] h-full w-full">
      <div className="flex justify-between">
        <div className="text-[1.5vw]">{purchaseOrSales} Summary</div>
        <div className="flex items-center justify-center">
          <div className="text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md  cursor-pointer flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1vw"
              height="1vw"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                fill="white"
              />
            </svg>
            <span className="ml-[3px]">
              <CSVLink
                filename={purchaseOrSales + " Summary Data"}
                className="no-hover"
                style={{ textDecoration: "none" }}
                data={downloadDataBody ?? []}
              >
                Download
              </CSVLink>
            </span>
          </div>
        </div>
      </div>
      <div className="cost-compliance-table mt-5">
        <TreeTable
          expandedKeys={expandedKeys}
          onToggle={(e) => {
            setExpandedKeys(e.value);
          }}
          emptyMessage={EmptyMessageTemplate}
          className="shadow-module-card"
          scrollHeight="47vh"
          scrollable
          value={treeData ?? []}
        >
          <Column
            field="name"
            header="Category"
            className="w-[17vw] custom-1700:w-[16vw]"
            expander
            body={(node) => {
              return <span>{node?.name}</span>;
            }}
          />
          <Column
            field="spend"
            header={purchaseOrSales === "Sales" ? "Sales" : "Spend"}
            body={(node) => {
              return spendBodyTemplate(node, totalSpend);
            }}
          />
          <Column
            field="volume"
            header="lbs."
            body={(node) => {
              return lbsBodyTemplate(node, totalVolume);
            }}
          />
        </TreeTable>
      </div>
    </div>
  );
};

export default PurchaseSummary;
